import { Link } from "react-router-dom";
import type { ReviewProgressItem, ReviewProgressStage } from "@compliance-sage/contracts";
import { api } from "../api/client";
import { useApiQuery } from "../hooks/useApiQuery";
import { useI18n, type MsgKey } from "../i18n";
import { ReportResultButton } from "../features/support/ReportResultButton";
import type { SourceGroupType } from "../features/settings/complianceScope/utils";
import { GROUP_ORDER } from "../features/settings/sageWorkflow/graphModel";
import { groupLabelKey } from "../features/settings/sageWorkflow/groupPresentation";
import {
  sourceGroupCounts,
  sourceGroupType,
} from "../features/settings/sageWorkflow/liveGraphModel";

export const ACTIVE_JOB_STATUSES = new Set(["PENDING", "RUNNING", "RETRYING"]);

/** Used only until enough real history exists to compute a historical p50. */
const DEFAULT_ESTIMATE_MS = 45_000;

export function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1_000));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return minutes > 0 ? `${minutes}m ${remainder}s` : `${remainder}s`;
}

/**
 * Reports only observable stage progress. Time-based fill used to race to 95%
 * and then stall during a long AI pass, which looked fast at first but made the
 * final wait feel broken. Historical duration remains useful for the ETA; it
 * should not manufacture completed work.
 */
function progressFraction(item: ReviewProgressItem): number {
  const stageIndex = ACTIVE_STAGES.indexOf(item.progressStage);
  return stageIndex === -1 ? 0 : (stageIndex + 0.5) / ACTIVE_STAGES.length;
}

export const ACTIVE_STAGES: ReviewProgressStage[] = [
  "INDEXING",
  "PREPARING",
  "DETERMINISTIC_CHECKS",
  "AI_REVIEW",
  "FINALIZING",
];

export function progressStageMessageKey(stage: ReviewProgressStage): MsgKey {
  const keys: Record<ReviewProgressStage, MsgKey> = {
    QUEUED: "analysisProgressQueued",
    INDEXING: "analysisProgressIndexing",
    PREPARING: "analysisProgressPreparing",
    DETERMINISTIC_CHECKS: "analysisProgressDeterministic",
    AI_REVIEW: "analysisProgressAnalyzing",
    FINALIZING: "analysisProgressFinalizing",
    COMPLETED: "analysisProgressComplete",
    FAILED: "analysisProgressFailed",
  };
  return keys[stage];
}

export function elapsedSince(item: ReviewProgressItem): string {
  return formatDuration(Date.now() - new Date(item.createdAt).getTime());
}

const SCOPE_TILE_CLASSES: Record<Exclude<SourceGroupType, "DICTIONARY">, string> = {
  POLICY: "bg-indigo-200 text-indigo-800",
  RULE_CUSTOM: "bg-emerald-200 text-emerald-800",
  RULE_TAUGHT: "bg-brand-200 text-brand-800",
};

const SCOPE_TILE_PREFIX: Record<Exclude<SourceGroupType, "DICTIONARY">, string> = {
  POLICY: "P",
  RULE_CUSTOM: "R",
  RULE_TAUGHT: "S",
};

/**
 * Renders one review's live analysis progress: stage stepper, source counts,
 * elapsed time, and the full list of policies, custom rules, Educate Sage
 * lessons, and dictionary entries locked for this run with a verdict once
 * available. Shared by the floating
 * dashboard widget (`ReviewProgressPanel`) and the review detail page, so a
 * lesson "try it" test and a normal review both get the same live view.
 */
export function ReviewProgressDetail({
  item,
  showOpenLink = false,
  sourcesExpandedByDefault = !showOpenLink,
  canReport = false,
  compact = false,
}: {
  item: ReviewProgressItem;
  showOpenLink?: boolean;
  sourcesExpandedByDefault?: boolean;
  /**
   * SUPPORT_CREATE, resolved by the caller. Defaults to false so the floating
   * widget and drawer — which render outside any auth or query context — keep
   * working without passing it.
   */
  canReport?: boolean;
  /** Compact, scope-focused presentation for the floating progress panel. */
  compact?: boolean;
}) {
  const { t, te } = useI18n();
  const active = ACTIVE_JOB_STATUSES.has(item.jobStatus);
  const currentStageIndex = ACTIVE_STAGES.indexOf(item.progressStage);
  // Historical benchmark, fetched (and cached across every rendered item) only
  // while something is actually active — a completed/failed item has no use
  // for a "time remaining" estimate.
  const estimate = useApiQuery(["reviewProgressEstimate"], () => api.reviewProgressEstimate(), {
    enabled: active,
  });
  const estimatedTotalMs = estimate.data?.p50DurationMs ?? DEFAULT_ESTIMATE_MS;
  const fraction = active ? progressFraction(item) : 1;
  const progressPercent = Math.round(fraction * 100);
  const remainingMs = estimatedTotalMs - (Date.now() - new Date(item.createdAt).getTime());
  const hasReliableEstimate = Boolean(estimate.data?.p50DurationMs);
  const counts = sourceGroupCounts(item);
  const sourceGroups = GROUP_ORDER.map((type) => ({
    type,
    items: item.sources
      .filter((source) => sourceGroupType(source) === type)
      .sort((a, b) => {
        const priority = { CANNOT_VERIFY: 0, ISSUE: 1, OK: 2, NOT_APPLICABLE: 3 };
        const aPriority = a.verdict ? priority[a.verdict] : 4;
        const bPriority = b.verdict ? priority[b.verdict] : 4;
        return aPriority - bPriority || a.title.localeCompare(b.title);
      }),
  }));
  const scopedSourceGroups = sourceGroups.filter(
    (group): group is typeof group & { type: Exclude<SourceGroupType, "DICTIONARY"> } =>
      group.type !== "DICTIONARY" && group.items.length > 0,
  );
  const scopedSourceCount = scopedSourceGroups.reduce((total, group) => total + group.items.length, 0);
  const targetName = [item.categoryParentName, item.categoryName].filter(Boolean).join(" › ") ||
    t("analysisScopeAllMaterials");

  if (compact) {
    return (
      <div className="min-w-0">
        <div className="flex items-start gap-2.5">
          <span
            className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 ${
              active
                ? "animate-spin border-brand-200 border-t-brand-600"
                : item.jobStatus === "FAILED"
                  ? "border-red-500 bg-red-50"
                  : "border-emerald-500 bg-emerald-50"
            }`}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-slate-900">
                {t(progressStageMessageKey(item.progressStage))}
              </p>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-brand-700">
                {progressPercent}%
              </span>
            </div>
            {active && (
              <div
                className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100"
                role="progressbar"
                aria-label={t("analysisProgressStages")}
                aria-valuenow={progressPercent}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full rounded-full bg-brand-500 transition-[width] duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            )}
            <p className="mt-1.5 text-[11px] text-slate-500">
              {t("analysisProgressElapsed", { time: elapsedSince(item) })}
              {active && (hasReliableEstimate ? remainingMs > 1_000 : true) && (
                <>
                  {" · "}
                  {hasReliableEstimate
                    ? t("analysisProgressEta", { time: formatDuration(remainingMs) })
                    : t("analysisProgressEtaUnknown")}
                </>
              )}
            </p>
          </div>
        </div>

        {canReport && item.jobStatus === "FAILED" && (
          <ReportResultButton
            anchor="ANALYSIS_FAILURE"
            reviewId={item.reviewId}
            subject={t("reportSubjectAnalysisFailure", { title: item.title })}
            labelKey="reportAnalysisFailure"
          />
        )}

        {scopedSourceCount > 0 && (
          <div className="mt-2.5 rounded-md bg-amber-50 p-2.5">
            <p className="text-xs font-semibold text-amber-950">
              {t("analysisScopeNudgeTitle", {
                channel: item.channelName,
                count: scopedSourceCount,
              })}
            </p>
            <p className="mt-0.5 text-[11px] leading-4 text-amber-900">
              {t("analysisScopeNudgeBody", { target: targetName })}
            </p>

            <div className="mt-2 border-t border-amber-200 pt-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold text-slate-700">{t("analysisScopeMapTitle")}</p>
                <p className="text-[10px] text-slate-500">{t("analysisScopeMapHint")}</p>
              </div>
              <div className="mt-1.5 space-y-1.5">
                {scopedSourceGroups.map((group, groupIndex) => (
                  <div
                    key={group.type}
                    className="grid grid-cols-[4.75rem_minmax(0,1fr)] items-start gap-2"
                  >
                    <p className="truncate pt-0.5 text-[10px] font-medium text-slate-600">
                      {t(groupLabelKey(group.type))} {group.items.length}
                    </p>
                    <div className="grid grid-cols-8 gap-1">
                      {group.items.map((source, index) => (
                        <span
                          key={source.referenceId}
                          className={`flex h-5 items-center justify-center rounded-sm text-[9px] font-semibold tabular-nums ${SCOPE_TILE_CLASSES[group.type]} ${active ? "cs-scope-tile-active" : ""}`}
                          style={active ? { animationDelay: `${groupIndex * 180 + index * 65}ms` } : undefined}
                          title={source.title}
                          aria-label={`${t(groupLabelKey(group.type))} ${index + 1}: ${source.title}`}
                        >
                          {SCOPE_TILE_PREFIX[group.type]}{index + 1}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {counts.DICTIONARY > 0 && (
                <p className="mt-1.5 text-[10px] text-slate-500">
                  {t("analysisScopeDictionaryNote", { count: counts.DICTIONARY })}
                </p>
              )}
            </div>

            <Link
              to="/sage-workflow?view=map"
              className="mt-2 inline-block text-[11px] font-semibold text-brand-700 hover:underline"
            >
              {t("analysisScopeReviewLink")} →
            </Link>
          </div>
        )}

        {showOpenLink && (
          <Link
            to={`/reviews/${item.reviewId}`}
            className="mt-2 inline-block text-xs font-semibold text-brand-700 hover:underline"
          >
            {t("analysisProgressOpen")} →
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-start gap-3">
      <span
        className={`mt-0.5 h-5 w-5 shrink-0 rounded-full border-2 ${
          active
            ? "animate-spin border-brand-200 border-t-brand-600"
            : item.jobStatus === "FAILED"
              ? "border-red-500 bg-red-50"
              : "border-emerald-500 bg-emerald-50"
        }`}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">{t(progressStageMessageKey(item.progressStage))}</p>
        {canReport && item.jobStatus === "FAILED" && (
          <ReportResultButton
            anchor="ANALYSIS_FAILURE"
            reviewId={item.reviewId}
            subject={t("reportSubjectAnalysisFailure", { title: item.title })}
            labelKey="reportAnalysisFailure"
          />
        )}
        <div className="mt-2 flex gap-1" aria-label={t("analysisProgressStages")}>
          {ACTIVE_STAGES.map((stage, index) => {
            const completed = item.progressStage === "COMPLETED" || index < currentStageIndex;
            const current = index === currentStageIndex;
            return (
              <span
                key={stage}
                title={t(progressStageMessageKey(stage))}
                className={`h-1.5 flex-1 rounded-full ${
                  item.progressStage === "FAILED" && index === 0
                    ? "bg-red-500"
                    : completed
                      ? "bg-emerald-500"
                      : current
                        ? "animate-pulse bg-brand-600"
                        : "bg-slate-200"
                }`}
              />
            );
          })}
        </div>
        {active && (
          <div
            className="mt-2 h-1 overflow-hidden rounded-full bg-slate-100"
            role="progressbar"
            aria-label={t("analysisProgressStages")}
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="h-full rounded-full bg-brand-400 transition-[width] duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
        <p className="mt-2 text-[11px] text-slate-500">
          {t("analysisProgressSourceCounts", {
            customRules: counts.RULE_CUSTOM,
            lessons: counts.RULE_TAUGHT,
            policies: counts.POLICY,
            dictionaryEntries: counts.DICTIONARY,
          })}{" "}
          ·{" "}
          {t("analysisProgressElapsed", { time: elapsedSince(item) })}
          {active && (hasReliableEstimate ? remainingMs > 1_000 : true) && (
            <>
              {" · "}
              {hasReliableEstimate
                ? t("analysisProgressEta", { time: formatDuration(remainingMs) })
                : t("analysisProgressEtaUnknown")}
            </>
          )}
        </p>
        {item.progressStage === "AI_REVIEW" && (
          <p className="mt-1 text-xs text-slate-500">{t("analysisProgressSinglePassNote")}</p>
        )}
        {item.sources.length > 0 && (
          <details className="mt-2 rounded-md bg-slate-50 px-2 py-1.5" open={sourcesExpandedByDefault}>
            <summary className="cursor-pointer text-xs font-semibold text-slate-600">
              {t("analysisProgressSources")}
            </summary>
            <div className="mt-2 space-y-2">
              {sourceGroups.map((group) =>
                group.items.length > 0 ? (
                  <section key={group.type}>
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                      {t(groupLabelKey(group.type))} ({group.items.length})
                    </h3>
                    <ul className="mt-1 divide-y divide-slate-200">
                      {group.items.map((source) => (
                        <li
                          key={source.referenceId}
                          className="flex items-start justify-between gap-2 py-1 text-xs"
                        >
                          <span className="min-w-0 break-words text-slate-600">{source.title}</span>
                          <span className="shrink-0 font-medium text-slate-500">
                            {source.verdict ? te("checkVerdict", source.verdict) : t("analysisProgressAwaitingResult")}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                ) : null,
              )}
            </div>
          </details>
        )}
        {showOpenLink && (
          <Link
            to={`/reviews/${item.reviewId}`}
            className="mt-2 inline-block text-xs font-semibold text-brand-700 hover:underline"
          >
            {t("analysisProgressOpen")} →
          </Link>
        )}
      </div>
    </div>
  );
}
